import { useEffect, useState } from 'react';
import { Calendar, CalendarDays, ClipboardList, CreditCard, Hash, RotateCcw, Settings, Wallet } from 'lucide-react';
import { GetGroupContractMandatsResponse, GroupContractDto } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { InfoItem } from 'components/display/InfoItem';
import { GenericLoading } from 'components/ui/Loader';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';

interface SpecialConditionsLockedSummaryProps {
  groupContractId: string;
}

/** Masque un IBAN style "FR76 **** **** **** **** **34". */
const maskIban = (iban: string | null | undefined): string | null => {
  if (!iban) return null;
  const trimmed = iban.replace(/\s+/g, '');
  if (trimmed.length < 8) return iban;
  return `${trimmed.slice(0, 4)} •••• •••• •••• •••• ${trimmed.slice(-4)}`;
};

/**
 * Résumé read-only des conditions particulières + mode de paiement + RIB.
 * Réutilise le design `PersonOverview` : en-tête avatar rond + `CardsSection` + `InfoItem`
 * + grid 2 colonnes avec divide-x.
 */
export const SpecialConditionsLockedSummary = ({ groupContractId }: SpecialConditionsLockedSummaryProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  const [contract, setContract] = useState<GroupContractDto | null>(null);
  const [mandats, setMandats] = useState<GetGroupContractMandatsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, m] = await Promise.all([
          contractsAPI.getGroupContractByID({ groupContractId }),
          contractsAPI.getGroupContractMandats({ groupContractId }),
        ]);
        if (!cancelled) {
          setContract(c);
          setMandats(m);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupContractId]);

  if (loading || !contract) return <GenericLoading loading isParentSized />;

  const isCadre = !!contract.isMaster;
  const selectedBank = mandats?.bankAccounts.find(b => b.id === mandats?.selectedBankAccountId);

  const effectiveDateStr = contract.effectiveDate
    ? new Date(contract.effectiveDate).toLocaleDateString('fr-FR')
    : null;

  return (
    <div className="space-y-6">
      {/* En-tête style PersonOverview */}
      <div className="flex items-center gap-4 pb-6 border-b border-primary/20">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Settings className="w-8 h-8 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold text-gray-900 truncate">
            {t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS_CARD.TITLE')}
          </h2>
          {effectiveDateStr && (
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              <span className="text-primary font-medium flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {effectiveDateStr}
              </span>
              {contract.mainDueDate && (
                <span className="text-gray-600 flex items-center gap-1">
                  <CalendarDays className="w-4 h-4" />
                  {contract.mainDueDate}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <CardsSection title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.SCHEDULING')}>
        <div className="grid grid-cols-2 divide-x">
          <div className="pr-6">
            <InfoItem
              icon={<Calendar />}
              label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.EFFECTIVE_DATE')}
              value={effectiveDateStr}
            />
            <InfoItem
              icon={<CalendarDays />}
              label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.MAIN_DUE_DATE')}
              value={contract.mainDueDate}
            />
          </div>
          <div className="pl-6">
            <InfoItem
              icon={<RotateCcw />}
              label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PERIODICITY')}
              value={contract.periodicity}
            />
            <InfoItem
              icon={<ClipboardList />}
              label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.NOTICE')}
              value={contract.noticePeriodDuration}
            />
          </div>
        </div>
      </CardsSection>

      {isCadre && (
        <CardsSection title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.PAYMENT_MODE')}>
          <InfoItem
            icon={<Wallet />}
            label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.PAYMENT_MODE')}
            value={
              mandats?.paymentByAliments
                ? t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PAYMENT_MODE.BY_ALIMENTS')
                : t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PAYMENT_MODE.BY_SUBSCRIBER')
            }
          />
        </CardsSection>
      )}

      {!mandats?.paymentByAliments && (
        <CardsSection title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.BANK_ACCOUNT')}>
          {selectedBank ? (
            <div className="grid grid-cols-2 divide-x">
              <div className="pr-6">
                <InfoItem
                  icon={<CreditCard />}
                  label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.BANK_ACCOUNT')}
                  value={selectedBank.label}
                />
              </div>
              <div className="pl-6">
                <InfoItem
                  icon={<Hash />}
                  label="IBAN"
                  value={maskIban(selectedBank.iban)}
                />
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Aucun compte bancaire renseigné.</p>
          )}
        </CardsSection>
      )}
    </div>
  );
};
